import { auditStatusRank, isAuditIssueStatus } from '../../admin/sync-monitoring/sync-monitoring-audit.utils';
import { IMonitoringEnvironment, IMonitoringErrorRow, IMonitoringErrorsParams, IMonitoringOverview } from './monitoring.models';

export type MonitoringPreset = 'allCurrent' | 'needsPerson' | 'retrying' | 'auditDrift' | 'sync' | 'outdated';

/** The preset chips, in display order. */
export const MONITORING_PRESETS: readonly MonitoringPreset[] = ['allCurrent', 'needsPerson', 'retrying', 'auditDrift', 'sync', 'outdated'];

/** What the operator should do about an error: `terminal` needs a person, `retrying` usually clears by itself, `outdated` is from before a version change. */
export type MonitoringGroup = 'terminal' | 'retrying' | 'outdated';

export interface IRowCounts {
	terminal: number;
	retrying: number;
	outdated: number;
}

export interface IAttentionLine {
	type: 'needsPerson' | 'oneCause';
	environment: IMonitoringEnvironment;
	/** Errors that need a person, or tables sharing the table-scope kind. */
	count: number;
	/** The kinds that need a person, or the one shared table-scope kind. */
	kinds: string[];
	/** The preset "Show in list" applies. */
	preset: MonitoringPreset;
	link: string;
}

export interface IMonitoringTreeRow extends IRowCounts {
	id: string;
	level: 'account' | 'environment' | 'table';
	name: string;
	mainCause: string | null;
	link: string;
	/** Whether the row is open; null for table rows, which have no toggle. */
	expanded: boolean | null;
	/** The overview entry of an environment row, null on other levels. */
	environment: IMonitoringEnvironment | null;
	/** The audit status: the table's, the environment's, or the worst of an account's environments; null when the roll-up has none. */
	auditStatus: string | null;
}

/** How an audit status reads: `ok` when aligned, `issue` when it does not confirm the data, `neutral` otherwise (in progress, One only, never audited). */
export function auditTone(status: string): 'ok' | 'issue' | 'neutral' {
	if (status === 'aligned') {
		return 'ok';
	}
	return isAuditIssueStatus(status) ? 'issue' : 'neutral';
}

const TONE_ORDER = ['issue', 'neutral', 'ok'];

/** The worst of the statuses, null when there are none: an issue before a neutral status before aligned, then One Monitor's order within a tone. A status this page does not know is neutral, so it never rolls up as aligned. */
function worstAuditStatus(statuses: readonly (string | null)[]): string | null {
	const rank = (status: string): [number, number] => [TONE_ORDER.indexOf(auditTone(status)), auditStatusRank(status)];
	const worse = (a: string, b: string): boolean => {
		const [toneA, orderA] = rank(a);
		const [toneB, orderB] = rank(b);
		return toneA < toneB || (toneA === toneB && orderA < orderB);
	};
	const present = statuses.filter((status): status is string => !!status);
	return present.length === 0 ? null : present.reduce((worst, status) => (worse(status, worst) ? status : worst));
}

/** A count of errors of one kind, terminal or not. */
interface ICause {
	kind: string;
	count: number;
	terminal: boolean;
}

const ONE_MONITOR_PATH = '/administration/sync-monitoring';
const ONE_CAUSE_MIN_TABLES = 3;

export function isAuditKind(kind: string): boolean {
	return kind.startsWith('audit');
}

export function errorGroup(isTerminal: boolean, outdated: boolean): MonitoringGroup {
	if (outdated) {
		return 'outdated';
	}
	return isTerminal ? 'terminal' : 'retrying';
}

/** Whether a count cell matches the preset's /errors filters. */
function presetMatches(preset: MonitoringPreset, kind: string, isTerminal: boolean): boolean {
	switch (preset) {
		case 'needsPerson':
			return isTerminal;
		case 'retrying':
			return !isTerminal;
		case 'auditDrift':
			return isAuditKind(kind);
		case 'sync':
			return !isAuditKind(kind);
		default:
			return true;
	}
}

/** The /errors filters of a preset for one environment; the kind presets send the environment's kinds of that category. */
export function presetParams(preset: MonitoringPreset, environment: IMonitoringEnvironment): IMonitoringErrorsParams {
	const kinds = environment.kinds.map((entry) => entry.kind);
	switch (preset) {
		case 'needsPerson':
			return { terminal: true };
		case 'retrying':
			return { terminal: false };
		case 'auditDrift':
			return { kind: kinds.filter(isAuditKind) };
		case 'sync':
			return { kind: kinds.filter((kind) => !isAuditKind(kind)) };
		case 'outdated':
			return { includeOutdated: true };
		default:
			return {};
	}
}

/** Needs a person descending, then retrying descending, then name. */
export function compareWorstFirst(a: Pick<IMonitoringTreeRow, 'terminal' | 'retrying' | 'name'>, b: Pick<IMonitoringTreeRow, 'terminal' | 'retrying' | 'name'>): number {
	return b.terminal - a.terminal || b.retrying - a.retrying || a.name.localeCompare(b.name);
}

/** The account's name from the user's accounts, else its id. */
export function accountName(accounts: readonly { id: string; name: string }[], accountId: string): string {
	return accounts.find((account) => account.id === accountId)?.name ?? accountId;
}

function oneMonitorLink(accountId: string, environmentId?: string, table?: string, kind?: string | null): string {
	const query = new URLSearchParams({ accountId });
	if (environmentId) {
		query.append('environmentId', environmentId);
	}
	if (table) {
		query.append('table', table);
	}
	if (kind) {
		query.append('kind', kind);
	}
	return `${ONE_MONITOR_PATH}?${query}`;
}

/** Among the kinds, a terminal kind first, then the kind with the most errors. */
function mainCause(causes: readonly ICause[]): string | null {
	const byKind = new Map<string, ICause>();
	for (const cause of causes) {
		const sum = byKind.get(cause.kind);
		byKind.set(cause.kind, { kind: cause.kind, count: (sum?.count ?? 0) + cause.count, terminal: !!sum?.terminal || cause.terminal });
	}
	const sorted = [...byKind.values()].sort((a, b) => Number(b.terminal) - Number(a.terminal) || b.count - a.count || a.kind.localeCompare(b.kind));
	return sorted[0]?.kind ?? null;
}

function countCauses(causes: readonly ICause[], outdated: boolean): IRowCounts {
	const counts: IRowCounts = { terminal: 0, retrying: 0, outdated: 0 };
	for (const cause of causes) {
		counts[errorGroup(cause.terminal, outdated)] += cause.count;
	}
	return counts;
}

/** The environment's current errors per kind that match the preset; the overview has no kinds for outdated errors. */
function environmentCauses(environment: IMonitoringEnvironment, preset: MonitoringPreset): ICause[] {
	if (preset === 'outdated') {
		return [];
	}
	return environment.kinds
		.flatMap((entry) => [
			{ kind: entry.kind, count: entry.terminal, terminal: true },
			{ kind: entry.kind, count: entry.retrying, terminal: false },
		])
		.filter((cause) => cause.count > 0 && presetMatches(preset, cause.kind, cause.terminal));
}

function tableCauses(row: IMonitoringErrorRow, preset: MonitoringPreset): ICause[] {
	return row.counts.filter((cell) => presetMatches(preset, cell.kind, cell.isTerminal)).map((cell) => ({ kind: cell.kind, count: cell.count, terminal: cell.isTerminal }));
}

function environmentRowId(environment: IMonitoringEnvironment): string {
	return `environment:${environment.accountId}/${environment.environmentId}`;
}

function accountRowId(accountId: string): string {
	return `account:${accountId}`;
}

/** The Needs attention lines: one per environment with errors that need a person, and one "One cause" line per environment where 3 or more current tables share a table-scope kind; needs a person first, then by count. `remainingRetryingTables` counts the retrying tables of the environments no line names. */
export function needsAttention(overview: IMonitoringOverview): { lines: IAttentionLine[]; remainingRetryingTables: number } {
	const lines: IAttentionLine[] = [];
	let remainingRetryingTables = 0;
	for (const environment of overview.environments) {
		const link = oneMonitorLink(environment.accountId, environment.environmentId);
		const terminal = environment.groups.terminal;
		if (terminal.errors > 0) {
			const kinds = environment.kinds.filter((entry) => entry.terminal > 0).sort((a, b) => b.terminal - a.terminal || a.kind.localeCompare(b.kind));
			lines.push({ type: 'needsPerson', environment, count: terminal.errors, kinds: kinds.map((entry) => entry.kind), preset: 'needsPerson', link });
		}
		const shared = environment.tableScopeKinds.filter((entry) => entry.tables >= ONE_CAUSE_MIN_TABLES).sort((a, b) => b.tables - a.tables || a.kind.localeCompare(b.kind))[0];
		if (shared) {
			lines.push({ type: 'oneCause', environment, count: shared.tables, kinds: [shared.kind], preset: 'allCurrent', link });
		}
		if (terminal.errors === 0 && !shared) {
			remainingRetryingTables += environment.groups.retrying.tables;
		}
	}
	lines.sort((a, b) => Number(b.type === 'needsPerson') - Number(a.type === 'needsPerson') || b.count - a.count || a.environment.environmentName.localeCompare(b.environment.environmentName));
	return { lines, remainingRetryingTables };
}

/**
 * The tree as table rows: accounts, then each expanded account's environments, then each expanded environment's loaded tables, all worst first. Account and environment rows come from the overview and show only the environments with errors under the preset; table rows come from `tables` (keyed by environment row id),
 * of which the Outdated preset keeps only the outdated rows.
 */
export function flattenTree(overview: IMonitoringOverview, preset: MonitoringPreset, expanded: ReadonlySet<string>, tables: ReadonlyMap<string, readonly IMonitoringErrorRow[]>, accounts: readonly { id: string; name: string }[]): IMonitoringTreeRow[] {
	const environmentsByAccount = new Map<string, { row: IMonitoringTreeRow; causes: ICause[] }[]>();
	for (const environment of overview.environments) {
		const causes = environmentCauses(environment, preset);
		const counts = preset === 'outdated' ? { terminal: 0, retrying: 0, outdated: environment.groups.outdated.errors } : countCauses(causes, false);
		if (counts.terminal + counts.retrying + counts.outdated === 0) {
			continue;
		}
		const id = environmentRowId(environment);
		const row: IMonitoringTreeRow = {
			...counts,
			id,
			level: 'environment',
			name: environment.environmentName,
			mainCause: mainCause(causes),
			link: oneMonitorLink(environment.accountId, environment.environmentId),
			expanded: expanded.has(id),
			environment,
			auditStatus: environment.auditStatus ?? null,
		};
		const list = environmentsByAccount.get(environment.accountId) ?? [];
		list.push({ row, causes });
		environmentsByAccount.set(environment.accountId, list);
	}

	const accountRows = [...environmentsByAccount].map(([accountId, environments]) => {
		const id = accountRowId(accountId);
		const row: IMonitoringTreeRow = {
			terminal: environments.reduce((sum, entry) => sum + entry.row.terminal, 0),
			retrying: environments.reduce((sum, entry) => sum + entry.row.retrying, 0),
			outdated: environments.reduce((sum, entry) => sum + entry.row.outdated, 0),
			id,
			level: 'account',
			name: accountName(accounts, accountId),
			mainCause: mainCause(environments.flatMap((entry) => entry.causes)),
			link: oneMonitorLink(accountId),
			expanded: expanded.has(id),
			environment: null,
			auditStatus: worstAuditStatus(environments.map((entry) => entry.row.auditStatus)),
		};
		return { row, environments: environments.map((entry) => entry.row).sort(compareWorstFirst) };
	});

	return accountRows.sort((a, b) => compareWorstFirst(a.row, b.row)).flatMap(({ row, environments }) => [row, ...(row.expanded ? environments.flatMap((environment) => [environment, ...(environment.expanded ? tableRows(environment, preset, tables) : [])]) : [])]);
}

function tableRows(environmentRow: IMonitoringTreeRow, preset: MonitoringPreset, tables: ReadonlyMap<string, readonly IMonitoringErrorRow[]>): IMonitoringTreeRow[] {
	const loaded = tables.get(environmentRow.id) ?? [];
	return loaded
		.filter((row) => preset !== 'outdated' || row.outdated)
		.map((row): IMonitoringTreeRow => {
			const causes = tableCauses(row, preset);
			const cause = mainCause(causes);
			return {
				...countCauses(causes, row.outdated),
				id: `table:${row.key}`,
				level: 'table',
				name: row.tableName,
				mainCause: cause,
				link: oneMonitorLink(row.accountId, row.environmentId, row.tableName, cause),
				expanded: null,
				environment: null,
				auditStatus: row.auditStatus ?? null,
			};
		})
		.sort(compareWorstFirst);
}
